import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Search, Brain, Sparkles, Target, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface DataPoint {
    id: string;
    x: number;
    y: number;
    label: string;
    category: 'animal' | 'food' | 'tech';
}

const DATA_POINTS: DataPoint[] = [
    { id: '1', x: 20, y: 20, label: 'Dog', category: 'animal' },
    { id: '2', x: 25, y: 25, label: 'Cat', category: 'animal' },
    { id: '3', x: 30, y: 20, label: 'Wolf', category: 'animal' },

    { id: '4', x: 70, y: 70, label: 'Pizza', category: 'food' },
    { id: '5', x: 75, y: 75, label: 'Burger', category: 'food' },
    { id: '6', x: 72, y: 80, label: 'Fries', category: 'food' },

    { id: '7', x: 20, y: 80, label: 'Computer', category: 'tech' },
    { id: '8', x: 25, y: 85, label: 'Robot', category: 'tech' },
    { id: '9', x: 15, y: 75, label: 'Phone', category: 'tech' },
];

const MISSIONS = [
    {
        id: 1,
        text: "Mission 1: Find a furry friend.",
        hint: "Type words like 'pet', 'bark', or use a specific animal name.",
        check: (label: string) => ['Dog', 'Cat', 'Wolf'].includes(label)
    },
    {
        id: 2,
        text: "Mission 2: Find a tasty meal.",
        hint: "Type words like 'hungry', 'lunch', or a food name.",
        check: (label: string) => ['Pizza', 'Burger', 'Fries'].includes(label)
    },
    {
        id: 3,
        text: "Mission 3: Find a digital device.",
        hint: "Type words like 'code', 'screen', or 'machine'.",
        check: (label: string) => ['Computer', 'Robot', 'Phone'].includes(label)
    },
];

export const VectorSearch = () => {
    const { completeModule } = useAcademyStore();
    const [query, setQuery] = useState('');
    const [searchPos, setSearchPos] = useState<{ x: number, y: number } | null>(null);
    const [nearest, setNearest] = useState<string | null>(null);
    const [completed, setCompleted] = useState(false);

    // Mission State
    const [missionIndex, setMissionIndex] = useState(0);
    const [missionComplete, setMissionComplete] = useState(false);
    const currentMission = MISSIONS[missionIndex];

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (missionComplete || completed) return; // Prevent spamming if verifying

        // Mock Vector Embedding Logic
        const q = query.toLowerCase();
        let targetX = 50;
        let targetY = 50;

        if (['dog', 'cat', 'animal', 'pet', 'wolf', 'fur', 'bark', 'meow'].some(w => q.includes(w))) {
            targetX = 25; targetY = 22; // Near animals
        } else if (['food', 'eat', 'hungry', 'pizza', 'burger', 'lunch', 'fries'].some(w => q.includes(w))) {
            targetX = 72; targetY = 75; // Near food
        } else if (['tech', 'computer', 'robot', 'code', 'phone', 'device', 'screen', 'machine'].some(w => q.includes(w))) {
            targetX = 20; targetY = 80; // Near tech
        } else {
            // Random-ish scatter for unknown
            targetX = 50 + (Math.random() * 20 - 10);
            targetY = 50 + (Math.random() * 20 - 10);
        }

        setSearchPos({ x: targetX, y: targetY });

        // Find nearest
        let minDist = 1000;
        let closest = '';
        DATA_POINTS.forEach(p => {
            const dist = Math.sqrt(Math.pow(p.x - targetX, 2) + Math.pow(p.y - targetY, 2));
            if (dist < minDist) {
                minDist = dist;
                closest = p.label;
            }
        });
        setNearest(closest);

        // Check Mission
        if (currentMission.check(closest)) {
            setMissionComplete(true);
            setTimeout(() => {
                if (missionIndex < MISSIONS.length - 1) {
                    setMissionIndex(prev => prev + 1);
                    setMissionComplete(false);
                    setQuery('');
                    setSearchPos(null);
                    setNearest(null);
                } else {
                    setCompleted(true);
                    completeModule('rag-basics', 100);
                }
            }, 2000);
        }
    };

    return (
        <div className="min-h-screen bg-[#f0f9ff] flex flex-col p-8 font-sans">
            <div className="mb-4 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-purple-600">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="bg-purple-100 text-purple-800 px-4 py-1 rounded-full text-sm font-bold border border-purple-200">
                    Step {missionIndex + 1}/{MISSIONS.length}: {completed ? 'Done' : 'Active'}
                </div>
            </div>

            <div className="text-center mb-8">
                <h1 className="text-4xl font-black font-comic text-slate-800 mb-2 flex items-center justify-center gap-3">
                    <Brain className="text-purple-600" size={40} />
                    Vector Search 🧠
                </h1>

                {/* Mission Banner */}
                <div className={`max-w-2xl mx-auto mb-6 p-4 rounded-xl border-2 transition-all duration-500 ${missionComplete ? 'bg-green-100 border-green-400 scale-105' : 'bg-white border-purple-400 shadow-lg'}`}>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        {missionComplete ? '✅ MISSION COMPLETE!' : '🎯 CURRENT OBJECTIVE'}
                    </div>
                    <div className={`text-xl font-bold ${missionComplete ? 'text-green-700' : 'text-purple-700'}`}>
                        {completed ? "All Missions Complete!" : currentMission.text}
                    </div>
                    {!completed && !missionComplete && (
                        <div className="text-sm text-slate-400 mt-1 italic">
                            Hint: {currentMission.hint}
                        </div>
                    )}
                </div>

                <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-slate-200 max-w-3xl mx-auto flex gap-4 text-left justify-center">
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 max-w-md">
                        <div className="font-bold text-purple-700 text-sm mb-1">What is a Vector?</div>
                        <div className="text-xs text-purple-600">
                            Computers turn words into list of numbers (coordinates). Similar meanings end up close together on the map!
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">

                {/* LEFT: Search Controls */}
                <div className="bg-white p-8 rounded-3xl shadow-xl border-4 border-slate-100 relative z-10">
                    <div className="absolute top-0 right-0 opacity-10 pointer-events-none p-4">
                        <img src="/assets/gorillas/gorilla_scientist.png" className="w-32" />
                    </div>

                    <h3 className="font-bold text-slate-700 mb-4">Semantic Search</h3>

                    <form onSubmit={handleSearch} className="space-y-4">
                        <input
                            type="text"
                            value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="Type a concept..."
                            disabled={missionComplete || completed}
                            className="w-full p-4 rounded-xl border-2 border-slate-200 focus:border-purple-500 outline-none font-bold text-lg disabled:bg-slate-50"
                        />
                        <button
                            type="submit"
                            disabled={!query || missionComplete || completed}
                            className="w-full bg-purple-600 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-purple-700 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search size={20} /> Embed & Search
                        </button>
                    </form>

                    {searchPos && (
                        <div className={`mt-8 p-4 rounded-xl border transition-colors ${missionComplete ? 'bg-green-100 border-green-300' : 'bg-purple-50 border-purple-200'}`}>
                            <div className="text-xs font-bold text-purple-400 uppercase mb-1">Result</div>
                            <div className="text-2xl font-black text-slate-800 flex items-center gap-2">
                                <Target className={missionComplete ? "text-green-500" : "text-red-500"} /> {nearest}
                            </div>
                            <div className="text-xs font-mono text-slate-500 mt-2">
                                Vector: [{searchPos.x.toFixed(0)}, {searchPos.y.toFixed(0)}]
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: Vector Space Visualizer */}
                <div className="lg:col-span-2 bg-slate-900 rounded-3xl p-8 h-[500px] shadow-2xl relative overflow-hidden border-4 border-slate-800">

                    <div className="absolute top-4 left-4 text-slate-500 font-mono text-xs">VECTOR SPACE VISUALIZER v1.0</div>
                    <div className="absolute top-4 right-4 text-slate-500 font-mono text-xs flex gap-4">
                        <span>X: Meaning (Nature ↔ Tech)</span>
                        <span>Y: Type (Living ↔ Object)</span>
                    </div>

                    {/* Grid Lines */}
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                    <div className="relative w-full h-full">
                        {/* Static Data Points */}
                        {DATA_POINTS.map(p => (
                            <motion.div
                                key={p.id}
                                className="absolute w-12 h-12 -ml-6 -mt-6 flex flex-col items-center justify-center group cursor-pointer z-10"
                                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                                whileHover={{ scale: 1.2, zIndex: 100 }}
                            >
                                <div className={`w-4 h-4 rounded-full ${p.category === 'animal' ? 'bg-orange-500' : p.category === 'food' ? 'bg-green-500' : 'bg-blue-500'} shadow-[0_0_10px_currentColor]`}></div>
                                <div className="text-xs text-white font-bold mt-1 bg-black/50 px-2 py-0.5 rounded backdrop-blur-sm whitespace-nowrap">
                                    {p.label}
                                </div>
                            </motion.div>
                        ))}

                        {/* Search Query Point */}
                        <AnimatePresence>
                            {searchPos && (
                                <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ left: `${searchPos.x}%`, top: `${searchPos.y}%`, scale: 1, opacity: 1 }}
                                    className="absolute w-8 h-8 -ml-4 -mt-4 z-20 pointer-events-none"
                                >
                                    <div className="w-full h-full rounded-full border-4 border-white bg-purple-500 animate-ping absolute opacity-50"></div>
                                    <div className="w-full h-full rounded-full border-2 border-white bg-purple-600 relative shadow-[0_0_20px_#a855f7] flex items-center justify-center">
                                        <Sparkles size={12} className="text-white" />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Legend */}
                    <div className="absolute bottom-4 right-4 flex gap-4">
                        <div className="flex items-center gap-2 text-xs text-white/70"><div className="w-2 h-2 rounded-full bg-orange-500"></div> Animals</div>
                        <div className="flex items-center gap-2 text-xs text-white/70"><div className="w-2 h-2 rounded-full bg-green-500"></div> Food</div>
                        <div className="flex items-center gap-2 text-xs text-white/70"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Tech</div>
                    </div>

                </div>

            </div>

            {/* Celebration */}
            {completed && (
                <ModuleSuccess
                    title="Vector Navigator"
                    onRetry={() => {
                        setCompleted(false);
                        setMissionIndex(0);
                        setQuery('');
                    }}
                    nextModulePath="/ai-agents"
                >
                    <div className="bg-purple-50 rounded-2xl p-6 text-left border-2 border-purple-200">
                        <h4 className="font-bold text-purple-800 mb-2">🧠 Neural Network Trained</h4>
                        <p className="text-purple-600 mb-4 text-sm">
                            You learned that AI maps similar meanings to similar locations.
                        </p>
                        <div className="space-y-2">
                            {MISSIONS.map(m => (
                                <div key={m.id} className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded">
                                    <CheckCircle size={12} /> {m.text}
                                </div>
                            ))}
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};

export default VectorSearch;
